import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
    Flag,
    User,
    Package,
    Clock,
    CheckCircle,
    XCircle,
    Eye,
    AlertTriangle,
    Loader2,
    ExternalLink,
    RefreshCw,
    ImageIcon
} from 'lucide-react';
import { formatDate, formatDateTime, getInitials, getResponsiveThumbnailUrl } from '@/lib/utils';
import { reportsAPI, adminAPI } from '@/lib/api';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';

const REASON_LABELS = {
    fake: 'Anunț fals/înșelător',
    fraud: 'Posibilă fraudă',
    counterfeit: 'Produs contrafăcut',
    inappropriate: 'Conținut nepotrivit',
    stolen: 'Obiect posibil furat',
    wrong_category: 'Categorie greșită',
    spam: 'Spam',
    other: 'Altul'
};

const STATUS_CONFIG = {
    pending: { label: 'În așteptare', variant: 'outline', color: 'text-yellow-600 bg-yellow-50' },
    investigating: { label: 'În investigare', variant: 'outline', color: 'text-blue-600 bg-blue-50' },
    resolved: { label: 'Rezolvat', variant: 'outline', color: 'text-green-600 bg-green-50' },
    dismissed: { label: 'Respins', variant: 'outline', color: 'text-gray-600 bg-gray-50' }
};

const AdminUserReportsTab = () => {
    const [reports, setReports] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('all');
    const [selectedReport, setSelectedReport] = useState(null);
    const [actionDialogOpen, setActionDialogOpen] = useState(false);
    const [adminNotes, setAdminNotes] = useState('');
    const [processing, setProcessing] = useState(false);
    const [newStatus, setNewStatus] = useState('');
    const [expandedUserNotes, setExpandedUserNotes] = useState({});
    const [addNoteDialogOpen, setAddNoteDialogOpen] = useState(false);
    const [selectedUserForNote, setSelectedUserForNote] = useState(null);
    const [newNote, setNewNote] = useState('');
    const [userFilter, setUserFilter] = useState('');
    const [reasonFilter, setReasonFilter] = useState('all');
    const [typeFilter, setTypeFilter] = useState('all');
    
    const fetchReports = async () => {
        setLoading(true);
        try {
            const res = await reportsAPI.getAll();
            setReports(res.data || []);
        } catch (error) {
            console.error('Error fetching reports:', error);
            toast.error('Eroare la încărcarea rapoartelor');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchReports();
    }, []);

    // Get unique users and reasons for filters
    const uniqueUsers = [...new Set(reports.map(r => r.target_username).filter(Boolean))].sort();
    const uniqueReasons = [...new Set(reports.map(r => r.reason).filter(Boolean))];

    // Count by type
    const typeCounts = {
        all: reports.length,
        listing: reports.filter(r => r.report_type === 'listing').length,
        user: reports.filter(r => r.report_type === 'user').length,
        admin_note: reports.filter(r => r.report_type === 'admin_note').length
    };

    const filteredReports = reports.filter(report => {
        // Status filter
        if (filter !== 'all' && report.status !== filter) return false;
        
        // User filter
        if (userFilter && report.target_username !== userFilter) return false;
        
        // Reason filter
        if (reasonFilter !== 'all' && report.reason !== reasonFilter) return false;
        
        // Type filter
        if (typeFilter !== 'all' && report.report_type !== typeFilter) return false;
        
        return true;
    });

    const handleOpenAction = (report, status) => {
        setSelectedReport(report);
        setNewStatus(status);
        setAdminNotes(report.admin_notes || '');
        setActionDialogOpen(true);
    };

    const handleUpdateStatus = async () => {
        if (!selectedReport) return;
        
        setProcessing(true);
        try {
            await reportsAPI.update(selectedReport.report_id, newStatus, adminNotes);
            toast.success('Raportul a fost actualizat');
            setActionDialogOpen(false);
            fetchReports();
        } catch (error) {
            console.error('Error updating report:', error);
            toast.error('Eroare la actualizarea raportului');
        } finally {
            setProcessing(false);
        }
    };

    const getCounts = () => {
        return {
            all: reports.length,
            pending: reports.filter(r => r.status === 'pending').length,
            investigating: reports.filter(r => r.status === 'investigating').length,
            resolved: reports.filter(r => r.status === 'resolved').length,
            dismissed: reports.filter(r => r.status === 'dismissed').length
        };
    };

    const counts = getCounts();

    // Get all notes grouped by target user, sorted chronologically within each user
    const getNotesByUser = () => {
        const userNotesMap = {};
        
        reports
            .filter(r => r.admin_notes && r.admin_notes.trim() !== '')
            .forEach(r => {
                const targetKey = r.target_user_id || r.target_username || 'unknown';
                const targetName = r.target_info?.name || r.target_username || 'Utilizator necunoscut';
                
                if (!userNotesMap[targetKey]) {
                    userNotesMap[targetKey] = {
                        target_user_id: r.target_user_id,
                        target_username: r.target_username,
                        target_name: targetName,
                        target_picture: r.target_info?.picture,
                        notes: []
                    };
                }
                
                userNotesMap[targetKey].notes.push({
                    report_id: r.report_id,
                    report_reason: REASON_LABELS[r.reason] || r.reason,
                    target_title: r.target_title || r.listing_title || 'N/A',
                    admin_notes: r.admin_notes,
                    admin_notes_by_name: r.admin_notes_by_name,
                    admin_notes_at: r.admin_notes_at,
                    status: r.status,
                    created_at: r.created_at
                });
            });
        
        // Sort notes within each user by date (newest first)
        Object.values(userNotesMap).forEach(user => {
            user.notes.sort((a, b) => {
                const dateA = a.admin_notes_at || a.created_at;
                const dateB = b.admin_notes_at || b.created_at;
                return new Date(dateB) - new Date(dateA);
            });
        });
        
        // Convert to array and sort by most recent note date
        return Object.values(userNotesMap).sort((a, b) => {
            const dateA = a.notes[0]?.admin_notes_at || a.notes[0]?.created_at;
            const dateB = b.notes[0]?.admin_notes_at || b.notes[0]?.created_at;
            return new Date(dateB) - new Date(dateA);
        });
    };

    const notesByUser = getNotesByUser();
    const totalNotesCount = notesByUser.reduce((acc, user) => acc + user.notes.length, 0);

    // Toggle notes visibility for a specific user
    const toggleUserNotes = (userId) => {
        setExpandedUserNotes(prev => ({
            ...prev,
            [userId]: !prev[userId]
        }));
    };

    // Open add note dialog for a user
    const handleOpenAddNote = (userGroup) => {
        setSelectedUserForNote(userGroup);
        setNewNote('');
        setAddNoteDialogOpen(true);
    };

    // Add a new note for a user
    const handleAddNote = async () => {
        if (!selectedUserForNote || !newNote.trim()) return;
        
        setProcessing(true);
        try {
            // Use the dedicated admin note endpoint
            await adminAPI.addUserAdminNote(selectedUserForNote.target_user_id, newNote);
            toast.success('Notița a fost adăugată');
            setAddNoteDialogOpen(false);
            setNewNote('');
            fetchReports();
        } catch (error) {
            console.error('Error adding note:', error);
            toast.error('Eroare la adăugarea notiței');
        } finally {
            setProcessing(false);
        }
    };

    if (loading) {
        return (
            <div className="space-y-4">
                <Skeleton className="h-10 w-full" />
                <div className="grid gap-4">
                    {[...Array(5)].map((_, i) => (
                        <Skeleton key={i} className="h-32 w-full rounded-xl" />
                    ))}
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-2xl font-bold flex items-center gap-2">
                        <Flag className="w-6 h-6 text-orange-500" />
                        Raportări
                    </h2>
                    <p className="text-muted-foreground mt-1">
                        Gestionează rapoartele pentru piese și utilizatori ({typeCounts.listing} piese, {typeCounts.user} utilizatori)
                    </p>
                </div>
                <Button variant="outline" onClick={fetchReports} className="rounded-full">
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Reîncarcă
                </Button>
            </div>

            {/* Filter Tabs */}
            <div className="flex flex-wrap gap-2">
                <Button
                    variant={filter === 'all' ? 'default' : 'outline-solid'}
                    size="sm"
                    onClick={() => setFilter('all')}
                    className="rounded-full"
                >
                    Toate ({counts.all})
                </Button>
                <Button
                    variant={filter === 'pending' ? 'default' : 'outline-solid'}
                    size="sm"
                    onClick={() => setFilter('pending')}
                    className="rounded-full"
                >
                    <Clock className="w-3 h-3 mr-1" />
                    În așteptare ({counts.pending})
                </Button>
                <Button
                    variant={filter === 'investigating' ? 'default' : 'outline-solid'}
                    size="sm"
                    onClick={() => setFilter('investigating')}
                    className="rounded-full"
                >
                    <Eye className="w-3 h-3 mr-1" />
                    În investigare ({counts.investigating})
                </Button>
                <Button
                    variant={filter === 'resolved' ? 'default' : 'outline-solid'}
                    size="sm"
                    onClick={() => setFilter('resolved')}
                    className="rounded-full text-green-600"
                >
                    <CheckCircle className="w-3 h-3 mr-1" />
                    Rezolvate ({counts.resolved})
                </Button>
                <Button
                    variant={filter === 'dismissed' ? 'default' : 'outline-solid'}
                    size="sm"
                    onClick={() => setFilter('dismissed')}
                    className="rounded-full text-gray-600"
                >
                    <XCircle className="w-3 h-3 mr-1" />
                    Respinse ({counts.dismissed})
                </Button>
            </div>

            {/* Advanced Filters */}
            <div className="flex flex-wrap gap-3 p-3 bg-muted/30 rounded-xl">
                <div className="flex items-center gap-2">
                    <User className="w-4 h-4 text-muted-foreground" />
                    <select 
                        value={userFilter}
                        onChange={(e) => setUserFilter(e.target.value)}
                        className="text-sm border rounded-lg px-3 py-1.5 bg-background focus:ring-2 focus:ring-primary/20 outline-hidden"
                    >
                        <option value="">Toți utilizatorii</option>
                        {uniqueUsers.map(user => (
                            <option key={user} value={user}>{user}</option>
                        ))}
                    </select>
                </div>
                
                <div className="flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-muted-foreground" />
                    <select 
                        value={reasonFilter}
                        onChange={(e) => setReasonFilter(e.target.value)}
                        className="text-sm border rounded-lg px-3 py-1.5 bg-background focus:ring-2 focus:ring-primary/20 outline-hidden"
                    >
                        <option value="all">Toate motivele</option>
                        {uniqueReasons.map(reason => (
                            <option key={reason} value={reason}>{REASON_LABELS[reason] || reason}</option>
                        ))}
                    </select>
                </div>
                
                <div className="flex items-center gap-2">
                    <Package className="w-4 h-4 text-muted-foreground" />
                    <select 
                        value={typeFilter}
                        onChange={(e) => setTypeFilter(e.target.value)}
                        className="text-sm border rounded-lg px-3 py-1.5 bg-background focus:ring-2 focus:ring-primary/20 outline-hidden"
                    >
                        <option value="all">Toate tipurile ({typeCounts.all})</option>
                        <option value="listing">Piese ({typeCounts.listing})</option>
                        <option value="user">Utilizatori ({typeCounts.user})</option>
                        <option value="admin_note">Notițe admin ({typeCounts.admin_note})</option>
                    </select>
                </div>
                
                {(userFilter || reasonFilter !== 'all' || typeFilter !== 'all') && (
                    <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => { setUserFilter(''); setReasonFilter('all'); setTypeFilter('all'); }}
                        className="text-muted-foreground hover:text-foreground"
                    >
                        <XCircle className="w-3 h-3 mr-1" />
                        Resetează filtre
                    </Button>
                )}
                
                <div className="ml-auto text-sm text-muted-foreground">
                    {filteredReports.length} raportări găsite
                </div>
            </div>

            {/* Reports List */}
            {filteredReports.length === 0 ? (
                <Card className="rounded-xl">
                    <CardContent className="py-12 text-center">
                        <Flag className="w-12 h-12 mx-auto text-muted-foreground/30 mb-4" />
                        <p className="text-muted-foreground">
                            {filter === 'all' ? 'Nu există raportări' : `Nu există raportări ${STATUS_CONFIG[filter]?.label.toLowerCase()}`}
                        </p>
                    </CardContent>
                </Card>
            ) : (
                <div className="space-y-4">
                    {filteredReports.map((report) => (
                        <Card key={report.report_id} className="rounded-xl hover:shadow-md transition-shadow">
                            <CardContent className="p-4">
                                <div className="flex items-start gap-4">
                                    {/* Target Preview - User or Listing */}
                                    <div className="shrink-0">
                                        {report.target_info?.type === 'user' ? (
                                            <Link to={`/user/${report.target_info.username}`} target="_blank">
                                                <Avatar className="w-16 h-16 border-2 border-border hover:border-primary transition-colors">
                                                    <AvatarImage src={report.target_info.picture} />
                                                    <AvatarFallback className="bg-primary/10 text-primary text-lg">
                                                        {getInitials(report.target_info.name || report.target_info.username)}
                                                    </AvatarFallback>
                                                </Avatar>
                                            </Link>
                                        ) : report.target_info?.type === 'listing' ? (
                                            <Link 
                                                to={`/listing/${report.target_info.slug || report.target_id}`}
                                                target="_blank"
                                                className="block"
                                            >
                                                <div className="w-16 h-16 rounded-lg border-2 border-border hover:border-primary transition-colors overflow-hidden bg-gray-100">
                                                    {report.target_info.image ? (
                                                        <img
                                                            src={getResponsiveThumbnailUrl(report.target_info.image, 100, 100, 75)}
                                                            alt={report.target_info.title}
                                                            className="w-full h-full object-cover"
                                                        />
                                                    ) : (
                                                        <div className="w-full h-full flex items-center justify-center">
                                                            <ImageIcon className="w-6 h-6 text-muted-foreground" />
                                                        </div>
                                                    )}
                                                </div>
                                            </Link>
                                        ) : (
                                            <div className="w-16 h-16 rounded-lg border-2 border-dashed border-muted flex items-center justify-center bg-muted/20">
                                                {report.report_type === 'user' ? (
                                                    <User className="w-6 h-6 text-muted-foreground" />
                                                ) : (
                                                    <Package className="w-6 h-6 text-muted-foreground" />
                                                )}
                                            </div>
                                        )}
                                    </div>
                                    
                                    {/* Report Info */}
                                    <div className="flex-1 min-w-0">
                                        {/* Target Name/Title */}
                                        {report.target_info && (
                                            <div className="mb-2">
                                                {report.target_info.type === 'user' ? (
                                                    <Link 
                                                        to={`/user/${report.target_info.username}`}
                                                        target="_blank"
                                                        className="font-semibold text-lg hover:text-primary transition-colors inline-flex items-center gap-2"
                                                    >
                                                        {report.target_info.name || report.target_info.username}
                                                        <span className="text-sm font-normal text-muted-foreground">
                                                            @{report.target_info.username}
                                                        </span>
                                                        <ExternalLink className="w-4 h-4" />
                                                    </Link>
                                                ) : (
                                                    <Link 
                                                        to={`/listing/${report.target_info.slug || report.target_id}`}
                                                        target="_blank"
                                                        className="font-semibold text-lg hover:text-primary transition-colors inline-flex items-center gap-2"
                                                    >
                                                        {report.target_info.title || 'Listing șters'}
                                                        <ExternalLink className="w-4 h-4" />
                                                    </Link>
                                                )}
                                            </div>
                                        )}
                                        
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <Badge className={STATUS_CONFIG[report.status]?.color || ''}>
                                                {STATUS_CONFIG[report.status]?.label || report.status}
                                            </Badge>
                                            <Badge variant="outline">
                                                {report.report_type === 'listing' ? (
                                                    <><Package className="w-3 h-3 mr-1" /> Listing</>
                                                ) : (
                                                    <><User className="w-3 h-3 mr-1" /> Utilizator</>
                                                )}
                                            </Badge>
                                            <Badge variant="secondary">
                                                {REASON_LABELS[report.reason] || report.reason}
                                            </Badge>
                                        </div>
                                        
                                        <div className="mt-3 space-y-1">
                                            <p className="text-sm">
                                                <span className="text-muted-foreground">Raportat de:</span>{' '}
                                                <Link 
                                                    to={`/user/${report.reporter_username}`}
                                                    target="_blank"
                                                    className="font-medium hover:text-primary hover:underline"
                                                >
                                                    {report.reporter_username || 'Anonim'}
                                                </Link>
                                            </p>
                                            {/* Show target user info when target_info is missing */}
                                            {!report.target_info && (report.target_username || report.target_id) && (
                                                <p className="text-sm">
                                                    <span className="text-muted-foreground">Utilizator raportat:</span>{' '}
                                                    {report.target_username ? (
                                                        <Link 
                                                            to={`/user/${report.target_username}`}
                                                            target="_blank"
                                                            className="text-primary hover:underline font-medium"
                                                        >
                                                            {report.target_username}
                                                        </Link>
                                                    ) : (
                                                        <code className="bg-muted px-1 py-0.5 rounded text-xs">{report.target_id}</code>
                                                    )}
                                                </p>
                                            )}
                                            {report.description && (
                                                <p className="text-sm mt-2 p-2 bg-muted/50 rounded-lg">
                                                    <span className="text-muted-foreground block text-xs mb-1">Descriere:</span>
                                                    {report.description}
                                                </p>
                                            )}
                                            {/* Report Images */}
                                            {report.images && report.images.length > 0 && (
                                                <div className="mt-3">
                                                    <span className="text-muted-foreground block text-xs mb-2">Imagini atașate ({report.images.length}):</span>
                                                    <div className="flex flex-wrap gap-2">
                                                        {report.images.map((imgUrl, idx) => (
                                                            <a 
                                                                key={idx} 
                                                                href={imgUrl.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${imgUrl}` : imgUrl}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="block"
                                                            >
                                                                <img
                                                                    src={imgUrl.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${imgUrl}` : imgUrl}
                                                                    alt={`Dovadă ${idx + 1}`}
                                                                    className="w-16 h-16 object-cover rounded-lg border hover:opacity-80 transition-opacity"
                                                                />
                                                            </a>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                            {report.admin_notes && (
                                                <div className="text-sm mt-2 p-2 bg-blue-50 rounded-lg border border-blue-100">
                                                    <div className="flex items-center justify-between mb-1">
                                                        <span className="text-blue-600 text-xs font-medium">Note admin:</span>
                                                        {report.admin_notes_by_name && (
                                                            <span className="text-xs text-blue-500">
                                                                de {report.admin_notes_by_name}
                                                                {report.admin_notes_at && ` • ${formatDate(report.admin_notes_at)}`}
                                                            </span>
                                                        )}
                                                    </div>
                                                    <p className="text-sm">{report.admin_notes}</p>
                                                </div>
                                            )}
                                        </div>
                                        
                                        <p className="text-xs text-muted-foreground mt-3">
                                            {formatDateTime(report.created_at)}
                                        </p>
                                    </div>

                                    {/* Actions */}
                                    <div className="flex flex-col gap-2">
                                        {report.status === 'pending' && (
                                            <>
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    className="rounded-full"
                                                    onClick={() => handleOpenAction(report, 'investigating')}
                                                >
                                                    <Eye className="w-3 h-3 mr-1" />
                                                    Investighează
                                                </Button>
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    className="rounded-full text-gray-600"
                                                    onClick={() => handleOpenAction(report, 'dismissed')}
                                                >
                                                    <XCircle className="w-3 h-3 mr-1" />
                                                    Respinge
                                                </Button>
                                            </>
                                        )}
                                        {report.status === 'investigating' && (
                                            <>
                                                <Button
                                                    size="sm"
                                                    className="rounded-full bg-green-600 hover:bg-green-700"
                                                    onClick={() => handleOpenAction(report, 'resolved')}
                                                >
                                                    <CheckCircle className="w-3 h-3 mr-1" />
                                                    Rezolvat
                                                </Button>
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    className="rounded-full text-gray-600"
                                                    onClick={() => handleOpenAction(report, 'dismissed')}
                                                >
                                                    <XCircle className="w-3 h-3 mr-1" />
                                                    Respinge
                                                </Button>
                                            </>
                                        )}
                                        {(report.status === 'resolved' || report.status === 'dismissed') && (
                                            <Button
                                                size="sm"
                                                variant="ghost"
                                                className="rounded-full text-muted-foreground"
                                                onClick={() => handleOpenAction(report, 'pending')}
                                            >
                                                <RefreshCw className="w-3 h-3 mr-1" />
                                                Redeschide
                                            </Button>
                                        )}
                                        
                                        {/* Notes buttons for this user */}
                                        {(() => {
                                            const userKey = report.target_user_id || report.target_username;
                                            const userNotes = notesByUser.find(u => 
                                                (u.target_user_id && u.target_user_id === report.target_user_id) || 
                                                (u.target_username === report.target_username)
                                            );
                                            const notesCount = userNotes?.notes?.length || 0;
                                            const isExpanded = expandedUserNotes[userKey];
                                            
                                            return (
                                                <>
                                                    <div className="border-t pt-2 mt-2">
                                                        {notesCount > 0 && (
                                                            <Button
                                                                size="sm"
                                                                variant="outline"
                                                                className="rounded-full w-full text-blue-600 border-blue-200 hover:bg-blue-50"
                                                                onClick={() => toggleUserNotes(userKey)}
                                                            >
                                                                <Clock className="w-3 h-3 mr-1" />
                                                                {isExpanded ? 'Ascunde' : 'Istoric'} note ({notesCount})
                                                            </Button>
                                                        )}
                                                        <Button
                                                            size="sm"
                                                            variant="outline"
                                                            className="rounded-full w-full mt-1"
                                                            onClick={() => handleOpenAddNote({
                                                                target_user_id: report.target_user_id,
                                                                target_username: report.target_username,
                                                                target_name: report.target_info?.name || report.target_username,
                                                                target_picture: report.target_info?.picture
                                                            })}
                                                        >
                                                            + Adaugă notiță
                                                        </Button>
                                                    </div>
                                                </>
                                            );
                                        })()}
                                    </div>
                                </div>
                                
                                {/* Expanded Notes Section for this user */}
                                {(() => {
                                    const userKey = report.target_user_id || report.target_username;
                                    const userNotes = notesByUser.find(u => 
                                        (u.target_user_id && u.target_user_id === report.target_user_id) || 
                                        (u.target_username === report.target_username)
                                    );
                                    const isExpanded = expandedUserNotes[userKey];
                                    
                                    if (!isExpanded || !userNotes?.notes?.length) return null;
                                    
                                    return (
                                        <div className="border-t bg-blue-50/50 p-3 mt-2">
                                            <p className="text-xs font-medium text-blue-700 mb-2">
                                                Toate notițele pentru {userNotes.target_name}:
                                            </p>
                                            <div className="space-y-2 max-h-60 overflow-y-auto">
                                                {userNotes.notes.map((note, idx) => (
                                                    <div key={`${note.report_id}-${idx}`} className="p-2 bg-white rounded-lg border border-blue-100 text-sm">
                                                        <p className="text-gray-800">{note.admin_notes}</p>
                                                        <div className="flex items-center justify-between mt-1 text-xs text-muted-foreground">
                                                            <span>
                                                                {note.admin_notes_by_name ? (
                                                                    <>de <strong>{note.admin_notes_by_name}</strong></>
                                                                ) : (
                                                                    <span className="italic">autor necunoscut</span>
                                                                )}
                                                            </span>
                                                            <span>
                                                                {note.admin_notes_at ? formatDateTime(note.admin_notes_at) : formatDate(note.created_at)}
                                                            </span>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    );
                                })()}
                            </CardContent>
                        </Card>
                    ))}
                </div>
            )}

            {/* Action Dialog */}
            <Dialog open={actionDialogOpen} onOpenChange={setActionDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>
                            {newStatus === 'investigating' && 'Începe investigarea'}
                            {newStatus === 'resolved' && 'Marchează ca rezolvat'}
                            {newStatus === 'dismissed' && 'Respinge raportul'}
                            {newStatus === 'pending' && 'Redeschide raportul'}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div>
                            <label className="text-sm font-medium mb-2 block">
                                Note admin (opțional)
                            </label>
                            <Textarea
                                value={adminNotes}
                                onChange={(e) => setAdminNotes(e.target.value)}
                                placeholder="Adaugă note despre acțiunea luată..."
                                rows={4}
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => setActionDialogOpen(false)}
                            className="rounded-full"
                        >
                            Anulează
                        </Button>
                        <Button
                            onClick={handleUpdateStatus}
                            disabled={processing}
                            className="rounded-full"
                        >
                            {processing && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                            Confirmă
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Add Note Dialog */}
            <Dialog open={addNoteDialogOpen} onOpenChange={setAddNoteDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            Adaugă notiță pentru {selectedUserForNote?.target_name}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        {selectedUserForNote && (
                            <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                                <Avatar className="w-10 h-10">
                                    <AvatarImage src={selectedUserForNote.target_picture} />
                                    <AvatarFallback>{getInitials(selectedUserForNote.target_name)}</AvatarFallback>
                                </Avatar>
                                <div>
                                    <p className="font-medium">{selectedUserForNote.target_name}</p>
                                    <p className="text-xs text-muted-foreground">@{selectedUserForNote.target_username}</p>
                                </div>
                            </div>
                        )}
                        <div>
                            <label className="text-sm font-medium mb-2 block">
                                Conținutul notiței
                            </label>
                            <Textarea
                                value={newNote}
                                onChange={(e) => setNewNote(e.target.value)}
                                placeholder="Scrie notița despre acest utilizator..."
                                rows={4}
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => setAddNoteDialogOpen(false)}
                            className="rounded-full"
                        >
                            Anulează
                        </Button>
                        <Button
                            onClick={handleAddNote}
                            disabled={processing || !newNote.trim()}
                            className="rounded-full"
                        >
                            {processing && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                            Adaugă notiță
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default AdminUserReportsTab;
